import { useState, useMemo, useEffect } from "react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import {
  useGetRoomAppointmentsQuery,
  useGetRoomResolvedAvailabilityForRangeQuery,
} from "../../store/services/roomsApi";
import { useGetClinicTherapistsQuery } from "../../store/services/therapistApi";
import { useGetTeamAvailabilityQuery } from "../../store/services/calendarApi";
import { isAssignableTherapist } from "../../config/permissionsRegistry";
import WeekView from "./WeekView";
import AppointmentModal from "./AppointmentEntryModal";
import {
  CaretLeft,
  CaretRight,
  CalendarBlank,
  Door,
  WheelchairMotion,
  UsersThree,
  PlusCircle,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import ContentLoader from "../shared/ContentLoader";
import "./RoomCalendarModal.css";

// Mobile breakpoint
const MOBILE_BREAKPOINT = 768;

// Exotic/unique colors for therapist availability lines
const THERAPIST_COLORS = [
  "#e91e63", // Pink
  "#00bcd4", // Cyan
  "#ff9800", // Orange
  "#9c27b0", // Purple
  "#4caf50", // Green
  "#f44336", // Red
  "#3f51b5", // Indigo
  "#ffeb3b", // Yellow
  "#795548", // Brown
  "#607d8b", // Blue Grey
];

/**
 * RoomCalendarModal - Modal showing a room's weekly calendar
 * Used by clinic admin to view room schedule and create appointments
 */
function RoomCalendarModal({ room, onClose, onAppointmentCreated }) {
  const clinicId = useSelector(selectClinicId);
  const adminTherapistId = useSelector(selectTherapistId);
  const adminTherapistName = useSelector(selectTherapistName);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [prefilledDate, setPrefilledDate] = useState(null);
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [selectedDayIndex, setSelectedDayIndex] = useState(new Date().getDay()); // Start on current day

  // Listen for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    trackFeature(FEATURE_KEYS.ROOMS);
  }, []);

  // Calculate date range for the week
  const dateRange = useMemo(() => {
    const current = new Date(currentDate);
    const dayOfWeek = current.getDay();
    const start = new Date(current);
    start.setDate(current.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [currentDate]);

  // Fetch appointments for the room
  const { data: appointments = [], isLoading, refetch: refetchAppointments } = useGetRoomAppointmentsQuery(
    {
      clinicId,
      roomId: room.id,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: !clinicId || !room.id }
  );

  // Room availability for the week — resolved through the full precedence
  // (always-available / exceptions / holidays / weekly template)
  const { data: resolvedRoomDays = [] } =
    useGetRoomResolvedAvailabilityForRangeQuery(
      {
        clinicId,
        roomId: room.id,
        startDate: dateRange.start.toISOString(),
        endDate: dateRange.end.toISOString(),
      },
      { skip: !clinicId || !room.id }
    );
  const availabilitySlots = useMemo(
    () =>
      resolvedRoomDays.map((day) => ({
        date: day.date,
        slots:
          day.mode === "slots"
            ? day.slots
            : day.mode === "open"
            ? [{ start: "07:00", end: "22:00" }]
            : [],
      })),
    [resolvedRoomDays]
  );

  // Fetch clinic therapists (for admin to select when creating appointments)
  const { data: clinicTherapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Get active, non-secretary therapists for availability display
  // (secretaries have no calendar / availability).
  // Colors cycle via THERAPIST_COLORS[index % length] — no cap on count.
  const activeTherapists = useMemo(() => {
    return clinicTherapists.filter(isAssignableTherapist);
  }, [clinicTherapists]);

  // Create therapist color map
  const therapistColorMap = useMemo(() => {
    const map = {};
    activeTherapists.forEach((therapist, index) => {
      map[therapist.uid] = {
        color: THERAPIST_COLORS[index % THERAPIST_COLORS.length],
        name: `${therapist.firstName} ${therapist.lastName}`,
        index,
      };
    });
    return map;
  }, [activeTherapists]);

  // Fetch availability for ALL active therapists in one batched query
  const activeTherapistIds = useMemo(
    () => activeTherapists.map((t) => t.uid).filter(Boolean),
    [activeTherapists]
  );
  const { data: teamAvailabilityRows = [] } = useGetTeamAvailabilityQuery(
    {
      therapistIds: activeTherapistIds,
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
    },
    { skip: activeTherapistIds.length === 0 }
  );

  // Combine all therapist availabilities with their colors
  // (same output shape as before; therapists with no slots are skipped)
  const therapistAvailabilities = useMemo(() => {
    const rowsById = {};
    teamAvailabilityRows.forEach((row) => {
      rowsById[row.therapistId] = row.slots || [];
    });

    const availabilities = [];
    activeTherapists.forEach((therapist) => {
      const data = rowsById[therapist.uid];
      if (therapist && data && data.length > 0) {
        availabilities.push({
          therapistId: therapist.uid,
          therapistName: `${therapist.firstName} ${therapist.lastName}`,
          color: therapistColorMap[therapist.uid]?.color,
          slots: data,
        });
      }
    });

    return availabilities;
  }, [teamAvailabilityRows, activeTherapists, therapistColorMap]);

  // Navigate to previous (week on desktop, day on mobile)
  const goToPrevious = () => {
    if (isMobile) {
      // Mobile: navigate to previous day
      if (selectedDayIndex > 0) {
        setSelectedDayIndex(selectedDayIndex - 1);
      } else {
        // Go to previous week, last day (Saturday)
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 7);
        setCurrentDate(newDate);
        setSelectedDayIndex(6);
      }
    } else {
      // Desktop: navigate to previous week
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  // Navigate to next (week on desktop, day on mobile)
  const goToNext = () => {
    if (isMobile) {
      // Mobile: navigate to next day
      if (selectedDayIndex < 6) {
        setSelectedDayIndex(selectedDayIndex + 1);
      } else {
        // Go to next week, first day (Sunday)
        const newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 7);
        setCurrentDate(newDate);
        setSelectedDayIndex(0);
      }
    } else {
      // Desktop: navigate to next week
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
    if (isMobile) {
      setSelectedDayIndex(new Date().getDay());
    }
  };

  // Handle slot click - open appointment modal with prefilled date and room
  const handleSlotClick = (date) => {
    setPrefilledDate(date);
    setSelectedAppointment(null);
    setShowAppointmentModal(true);
  };

  // Handle appointment click - open modal for viewing/editing
  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setPrefilledDate(null);
    setShowAppointmentModal(true);
  };

  // Close appointment modal
  const handleCloseAppointmentModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    setPrefilledDate(null);
  };

  // Handle successful appointment creation - refetch room appointments and notify parent
  const handleAppointmentSuccess = () => {
    refetchAppointments();
    if (onAppointmentCreated) {
      onAppointmentCreated();
    }
  };

  // Format header date display
  const formatHeaderDate = () => {
    const weekStart = dateRange.start;
    const weekEnd = dateRange.end;

    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${weekStart.getDate()}-${weekEnd.getDate()} ${weekStart.toLocaleDateString(
        "he-IL",
        { month: "long", year: "numeric" }
      )}`;
    } else {
      const startStr = weekStart.toLocaleDateString("he-IL", {
        day: "numeric",
        month: "long",
      });
      const endStr = weekEnd.toLocaleDateString("he-IL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return `${startStr} - ${endStr}`;
    }
  };

  // Get first assignable (active, non-secretary) therapist as target
  // (admin scheduling mode)
  const firstActiveTherapist = useMemo(() => {
    return clinicTherapists.find(isAssignableTherapist);
  }, [clinicTherapists]);

  return (
    <div className="room-calendar-modal-overlay" onClick={onClose}>
      <div
        className="room-calendar-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="room-calendar-modal-header">
          <div className="header-right">
            <div className="room-info">
              <div className="room-avatar">
                <Door size={28} weight="fill" />
              </div>
              <div className="room-details">
                <h2>{room.name}</h2>
                <div className="room-badges">
                  {room.isAccessible && (
                    <span className="room-badge accessible">
                      <WheelchairMotion size={14} weight="bold" />
                      נגיש
                    </span>
                  )}
                  <span className="room-badge capacity">
                    <UsersThree size={14} weight="bold" />
                    קיבולת: {room.capacity || 1}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="header-center">
            <div className="date-navigation">
              <button className="nav-btn" onClick={goToPrevious}>
                <CaretRight size={18} weight="bold" />
              </button>
              <button className="today-btn" onClick={goToToday}>
                <CalendarBlank size={16} weight="regular" />
                <span>היום</span>
              </button>
              <button className="nav-btn" onClick={goToNext}>
                <CaretLeft size={18} weight="bold" />
              </button>
            </div>
            <h3 className="current-date-display">{formatHeaderDate()}</h3>
          </div>

          <div className="header-left">
            <ModalCloseButton onClick={onClose} />
          </div>
        </div>

        {/* Modal Body - Week View (Day View on mobile) */}
        <div className="room-calendar-modal-body">
          {isLoading ? (
            <ContentLoader fill message="טוען יומן חדר..." />
          ) : (
            <WeekView
              startDate={dateRange.start}
              appointments={appointments}
              availabilitySlots={availabilitySlots}
              onSlotClick={handleSlotClick}
              onAppointmentClick={handleAppointmentClick}
              showTherapistName={true}
              therapistAvailabilities={therapistAvailabilities}
              singleDayMode={isMobile}
              selectedDayIndex={selectedDayIndex}
            />
          )}
        </div>

        {/* Footer */}
        <div className="room-calendar-modal-footer">
          <div className="footer-top-row">
            <div className="info-note">
              <PlusCircle size={16} weight="fill" />
              <span>לחץ על משבצת ירוקה ליצירת פגישה חדשה</span>
            </div>
            <div className="appointments-count">
              <CalendarBlank size={16} weight="fill" />
              <span>{appointments.length} פגישות השבוע</span>
            </div>
          </div>

          {/* Therapist availability legend */}
          {therapistAvailabilities.length > 0 && (
            <div className="therapist-availability-legend">
              <span className="legend-title">זמינות מטפלים:</span>
              <div className="legend-items">
                {therapistAvailabilities.map((ta) => (
                  <div key={ta.therapistId} className="legend-item">
                    <span
                      className="legend-color-bar"
                      style={{ backgroundColor: ta.color }}
                    ></span>
                    <span className="legend-name">{ta.therapistName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Appointment Modal */}
        {showAppointmentModal && (
          <AppointmentModal
            appointment={selectedAppointment}
            prefilledDate={prefilledDate}
            patients={[]} // Will be loaded dynamically by admin therapist selection
            onClose={handleCloseAppointmentModal}
            // Pass target therapist info for admin scheduling
            targetTherapistId={firstActiveTherapist?.uid || adminTherapistId}
            targetTherapistName={
              firstActiveTherapist
                ? `${firstActiveTherapist.firstName} ${firstActiveTherapist.lastName}`
                : adminTherapistName
            }
            scheduledByAdminId={adminTherapistId}
            scheduledByAdminName={adminTherapistName}
            // Pass pre-selected room
            preSelectedRoomId={room.id}
            // Callback when appointment is successfully created - refetch room appointments and notify parent
            onSuccess={handleAppointmentSuccess}
          />
        )}
      </div>
    </div>
  );
}

export default RoomCalendarModal;
